import React, { useEffect, useState } from "react";
import "../../App.css";
import { useNavigate, Link } from "react-router-dom";
import BACKEND_URL from "../../config/config";
import {
  getRuntimeFeatureFlags,
  persistFeatureFlags,
  resolveFeatureFlags,
} from "../../config/featureFlags";
import EconoJournalLogo from "../../components/EconoJournalLogo";
import { useLanguage } from "../../context/LanguageContext";

function App() {
  const { language } = useLanguage();
  const getStoredValue = (key, defaultValue = "") =>
    sessionStorage.getItem(key) || defaultValue;

  const setStoredValue = (key, value) => {
    sessionStorage.setItem(key, value);
  };

  const t = {
    EN: {
      title: "Public Form",
      subtitle:
        "This information will be used to understand the situation identified by participants and prepare journalistic reports.",
      q1: "What do you currently consider the main challenge to sustaining a new stage of expansion in Argentina's electricity market?",
      q2: "Which technology should play a greater role in the electricity sector's upcoming investments?",
      q3: "How confident are you that the electricity market reform will generate new private investments over the next three years?",
      q4: "What content are you interested in receiving from EconoJournal?",
      specifyOther: "Tell us which one",
      otherRequired: "Please specify the selected option.",
      selectOption: "Select an option",
      selectMultiple: "Select one or more options",
      noOptions: "No options",
      required: "*Required fields",
      legal: "By submitting this form, you agree to our ",
      privacy: "Privacy Policy.",
      back: "Back",
      sending: "Submitting...",
      confirm: "Confirm",
      submitError: "We couldn't process your registration.",
      submitCatch: "An error occurred while submitting your registration. Please try again.",
      footer: "Design, code, words, and hard work by ",
      q1Options: [
        "Expanding the electricity transmission grid",
        "Ensuring stable rules and updating the sector's regulatory framework",
        "Facilitating financing for new projects",
        "Normalizing and deepening private contracting",
        "Adding new generation capacity",
        "Other [Specify]",
      ],
      q2Options: [
        "Renewable energy",
        "Natural gas-fired thermal generation",
        "Battery energy storage systems",
        "Hybrid renewable energy and battery projects",
        "Nuclear energy projects",
        "A balanced combination of different technologies",
      ],
      q3Options: [
        "Very high",
        "High",
        "Moderate",
        "Low",
        "Very low",
        "I still do not have enough information to assess it",
      ],
      q4Options: [
        "Midstream (transportation, infrastructure)",
        "Production and upstream",
        "Natural gas and LNG",
        "Regulation and public policies",
        "Innovation and technology",
        "Energy market",
        "Sustainability and emissions",
        "Financing and investments",
        "Other [Specify]",
      ],
    },
    ES: {
      title: "Formulario de público",
      subtitle:
        "Esta información será utilizada para comprender la situación que detectan nuestros participantes y elaborar informes periodísticos.",
      q1: "¿Cuál considerás que es hoy el principal desafío para sostener una nueva etapa de expansión del mercado eléctrico argentino?",
      q2: "¿Qué tecnología debería tener mayor protagonismo en las próximas inversiones del sector eléctrico?",
      q3: "¿Qué grado de confianza tenés en que la reforma del mercado eléctrico genere nuevas inversiones privadas durante los próximos tres años?",
      q4: "¿Qué contenido te interesa recibir de EconoJournal?",
      specifyOther: "Contanos cuál",
      otherRequired: "Especificá la opción seleccionada.",
      selectOption: "Seleccioná una opción",
      selectMultiple: "Seleccioná una o más opciones",
      noOptions: "Sin opciones",
      required: "*Los campos son requeridos",
      legal: "Al enviar este formulario, aceptás nuestra ",
      privacy: "Política de Privacidad.",
      back: "Volver",
      sending: "Enviando...",
      confirm: "Confirmar",
      submitError: "No pudimos procesar tu inscripción.",
      submitCatch: "Ocurrió un error enviando tu inscripción. Probá nuevamente.",
      footer: "Design, code, words, and hard work by ",
      q1Options: [
        "Ampliar la red de transporte eléctrico",
        "Asegurar reglas estables y actualizar el marco regulatorio del sector",
        "Facilitar el financiamiento de nuevos proyectos",
        "Normalizar y profundizar la contratación entre privados",
        "Incorporar nueva capacidad de generación",
        "Otro [Especificar]",
      ],
      q2Options: [
        "Energías renovables",
        "Generación térmica a gas natural",
        "Sistemas de almacenamiento con baterías",
        "Proyectos híbridos de renovables y baterías",
        "Proyectos de energía nuclear",
        "Una combinación equilibrada de distintas tecnologías",
      ],
      q3Options: [
        "Muy alto",
        "Alto",
        "Moderado",
        "Bajo",
        "Muy bajo",
        "Todavía no cuento con suficiente información para evaluarlo",
      ],
      q4Options: [
        "Midstream (transporte, infraestructura)",
        "Producción y upstream",
        "Gas natural y LNG",
        "Regulación y políticas públicas",
        "Innovación y tecnología",
        "Mercado energético",
        "Sustentabilidad y emisiones",
        "Financiamiento e inversiones",
        "Otro [Especificar]",
      ],
    },
  };

  const getEmissionValue = (data, keys) => {
    for (const key of keys) {
      const value = key
        .split(".")
        .reduce((current, part) => current?.[part], data);
      const number = Number(value);

      if (Number.isFinite(number)) return number;
    }

    return null;
  };

  const persistEmissionBreakdown = (data) => {
    const air = getEmissionValue(data, [
      "co2_air_user",
      "co2_air",
      "air_emission",
      "air_emissions",
      "emissions.co2_air_user",
      "emissions.air",
    ]);
    const land = getEmissionValue(data, [
      "co2_land_user",
      "co2_transport_user",
      "co2_land",
      "land_emission",
      "land_emissions",
      "emissions.co2_land_user",
      "emissions.land",
    ]);
    const hotel = getEmissionValue(data, [
      "co2_hotel_user",
      "co2_hotel",
      "hotel_emission",
      "hotel_emissions",
      "emissions.co2_hotel_user",
      "emissions.hotel",
    ]);

    if (air !== null) sessionStorage.setItem("co2_air_user", String(air));
    if (land !== null) sessionStorage.setItem("co2_land_user", String(land));
    if (hotel !== null) sessionStorage.setItem("co2_hotel_user", String(hotel));
  };

  const [questionsECJ1, setQuestionsECJ1] = useState(
    getStoredValue("questions_ECJ_1")
  );
  const [questionsECJ2, setQuestionsECJ2] = useState(
    getStoredValue("questions_ECJ_2")
  );
  const [questionsECJ3, setQuestionsECJ3] = useState(
    getStoredValue("questions_ECJ_3")
  );
  const [questionsECJ4, setQuestionsECJ4] = useState(
    getStoredValue("questions_ECJ_4")
  );
  const [questionsECJ1Other, setQuestionsECJ1Other] = useState(
    getStoredValue("questions_ECJ_1_other")
  );
  const [questionsECJ4Other, setQuestionsECJ4Other] = useState(
    getStoredValue("questions_ECJ_4_other")
  );
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [runtimeFlags, setRuntimeFlags] = useState(getRuntimeFeatureFlags);

  const navigate = useNavigate();
  const { IS_FREE, ENABLE_QUESTIONS, ENABLE_EMISSION } = runtimeFlags;

  useEffect(() => {
    let cancelled = false;

    const loadPublicConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/public-config`);
        if (!res.ok) throw new Error("No se pudo cargar public-config");

        const data = await res.json();
        if (cancelled) return;

        persistFeatureFlags(data);
        setRuntimeFlags(resolveFeatureFlags(data));
      } catch (error) {
        console.error("Error cargando public-config:", error);
      }
    };

    loadPublicConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  const btnBack = () => {
    if (ENABLE_EMISSION) navigate("/emission");
    else navigate("/registration");
  };

  const isQuestion1Other = (value) =>
    value === "Other [Specify]" || value === "Otro [Especificar]";

  const isQuestion4Other = (value) =>
    value === "Other [Specify]" || value === "Otro [Especificar]";

  const collectPayload = () => {
    const pick = (k, def = "") => sessionStorage.getItem(k) || def;

    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const codeIsValid = sessionStorage.getItem("code_is_valid") === "true";
    const hotelFlag = toNum(pick("stayed_in_hotel"));
    const nights = toNum(pick("hotel_nights"));

    return {
      first_name: pick("first_name"),
      last_name: pick("last_name"),
      dni: pick("dni"),
      email: pick("email"),
      phone: pick("phone"),
      position: pick("position"),
      region: pick("region"),
      sector: pick("sector"),
      employees: pick("employees"),
      id_company: pick("id_company") ? parseInt(pick("id_company"), 10) : null,
      company_name: pick("company_name") || null,
      event_source: pick("event_source"),
      event_source_other: pick("event_source_other"),
      invitation_code:
        !IS_FREE && codeIsValid
          ? (pick("invitation_code") || "").toUpperCase().trim()
          : "",
      panel_topics: pick("panel_topics"),
      air_travel: pick("air_travel", "0"),
      travel_time: toNum(pick("travel_time", "0")),
      event_transport: pick("event_transport", "N/A"),
      km_land: toNum(pick("km_land", "0")),
      stayed_in_hotel: hotelFlag === 1 ? 1 : 0,
      hotel_nights: hotelFlag === 1 ? nights : 0,
      extra_answers: {
        questions_emissions_1: pick("questions_emissions_1"),
        questions_emissions_2: pick("questions_emissions_2"),
        questions_emissions_3: pick("questions_emissions_3"),
        questions_ECJ_1: pick("questions_ECJ_1"),
        questions_ECJ_1_other: pick("questions_ECJ_1_other"),
        questions_ECJ_2: pick("questions_ECJ_2"),
        questions_ECJ_3: pick("questions_ECJ_3"),
        questions_ECJ_4: pick("questions_ECJ_4"),
        questions_ECJ_4_other: pick("questions_ECJ_4_other"),
      },
      inscripto: false,
    };
  };

  const btnNext = async (e) => {
    e.preventDefault();

    if (!ENABLE_QUESTIONS) {
      const codeIsValid = sessionStorage.getItem("code_is_valid") === "true";
      const shouldPay = !IS_FREE && !codeIsValid;

      navigate(shouldPay ? "/summary" : "/thanks");
      return;
    }

    const errores = {};

    if (!questionsECJ1.trim()) {
      errores.questionsECJ1 = t[language].selectOption;
    }

    if (isQuestion1Other(questionsECJ1) && !questionsECJ1Other.trim()) {
      errores.questionsECJ1Other = t[language].otherRequired;
    }

    if (!questionsECJ2.trim()) {
      errores.questionsECJ2 = t[language].selectOption;
    }

    if (!questionsECJ3.trim()) {
      errores.questionsECJ3 = t[language].selectOption;
    }

    if (!questionsECJ4.trim()) {
      errores.questionsECJ4 = t[language].selectOption;
    }

    if (isQuestion4Other(questionsECJ4) && !questionsECJ4Other.trim()) {
      errores.questionsECJ4Other = t[language].otherRequired;
    }

    setFormErrors(errores);

    if (Object.keys(errores).length > 0) return;
    if (submitting) return;

    try {
      setSubmitting(true);
      setSubmitError("");

      const payload = collectPayload();

      const res = await fetch(`${BACKEND_URL}/responses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.status === "error") {
        setSubmitError(data?.code || data?.message || t[language].submitError);
        return;
      }

      const co2 =
        data?.co2_total_per_user ??
        data?.co2_emitted_user ??
        data?.emissions_kg_co2e;
      const co2Number = Number(co2);

      if (Number.isFinite(co2Number)) {
        localStorage.setItem("emissions_kg_co2e", String(co2Number));
        sessionStorage.setItem("co2_total_per_user", String(co2Number));
      }

      persistEmissionBreakdown(data);

      if (data?.user_id) {
        localStorage.setItem("user_id", String(data.user_id));
        sessionStorage.setItem("user_id", String(data.user_id));
      }

      const email = (sessionStorage.getItem("email") || "").trim();
      if (email) localStorage.setItem("email", email);

      const codeIsValid = sessionStorage.getItem("code_is_valid") === "true";
      const shouldPay = !IS_FREE && !codeIsValid;

      navigate(shouldPay ? "/summary" : "/thanks");
    } catch (err) {
      console.error(err);
      setSubmitError(t[language].submitCatch);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <div className="flexBetween">
        <EconoJournalLogo className="logoECJ" />
        <img
          className="logoSvant"
          src="/img/svant-logo.svg"
          alt="Svant Measure and Act"
        />
      </div>

      <h2>{t[language].title}</h2>
      <h4>{t[language].subtitle}</h4>

      <form onSubmit={btnNext}>
        <div className="containerFlex">
          <label>
            {t[language].q1} <b>*</b>
          </label>

          <select
            value={questionsECJ1}
            name="questions_ECJ_1"
            onChange={(e) => {
              setQuestionsECJ1(e.target.value);
              setStoredValue("questions_ECJ_1", e.target.value);
              if (!isQuestion1Other(e.target.value)) {
                setQuestionsECJ1Other("");
                sessionStorage.removeItem("questions_ECJ_1_other");
              }
            }}
          >
            <option value="" disabled>
              {t[language].selectOption}
            </option>
            {t[language].q1Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {formErrors.questionsECJ1 && (
            <span className="required">{formErrors.questionsECJ1}</span>
          )}

          {isQuestion1Other(questionsECJ1) && (
            <>
              <input
                type="text"
                value={questionsECJ1Other}
                onChange={(e) => {
                  setQuestionsECJ1Other(e.target.value);
                  setStoredValue("questions_ECJ_1_other", e.target.value);
                }}
                placeholder={t[language].specifyOther}
              />
              {formErrors.questionsECJ1Other && (
                <span className="required">{formErrors.questionsECJ1Other}</span>
              )}
            </>
          )}

          <label>
            {t[language].q2} <b>*</b>
          </label>

          <select
            value={questionsECJ2}
            name="questions_ECJ_2"
            onChange={(e) => {
              setQuestionsECJ2(e.target.value);
              setStoredValue("questions_ECJ_2", e.target.value);
            }}
          >
            <option value="" disabled>
              {t[language].selectOption}
            </option>
            {t[language].q2Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {formErrors.questionsECJ2 && (
            <span className="required">{formErrors.questionsECJ2}</span>
          )}

          <label>
            {t[language].q3} <b>*</b>
          </label>

          <select
            value={questionsECJ3}
            name="questions_ECJ_3"
            onChange={(e) => {
              setQuestionsECJ3(e.target.value);
              setStoredValue("questions_ECJ_3", e.target.value);
            }}
          >
            <option value="" disabled>
              {t[language].selectOption}
            </option>
            {t[language].q3Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {formErrors.questionsECJ3 && (
            <span className="required">{formErrors.questionsECJ3}</span>
          )}

          <label>
            {t[language].q4} <b>*</b>
          </label>

          <select
            value={questionsECJ4}
            name="questions_ECJ_4"
            onChange={(e) => {
              setQuestionsECJ4(e.target.value);
              setStoredValue("questions_ECJ_4", e.target.value);
              if (!isQuestion4Other(e.target.value)) {
                setQuestionsECJ4Other("");
                sessionStorage.removeItem("questions_ECJ_4_other");
              }
            }}
          >
            <option value="" disabled>
              {t[language].selectOption}
            </option>
            {t[language].q4Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {formErrors.questionsECJ4 && (
            <span className="required">{formErrors.questionsECJ4}</span>
          )}

          {isQuestion4Other(questionsECJ4) && (
            <>
              <input
                type="text"
                value={questionsECJ4Other}
                onChange={(e) => {
                  setQuestionsECJ4Other(e.target.value);
                  setStoredValue("questions_ECJ_4_other", e.target.value);
                }}
                placeholder={t[language].specifyOther}
              />
              {formErrors.questionsECJ4Other && (
                <span className="required">{formErrors.questionsECJ4Other}</span>
              )}
            </>
          )}
        </div>

        <div>
          <p className="legal">{t[language].required}</p>
        </div>

        <div className="containerFlex">
          <div className="card flexBetween">
            <div>
              <p className="legal">
                {t[language].legal}
                <Link to="/privacy" target="_blank">
                  {t[language].privacy}
                </Link>
              </p>
            </div>

            {submitError && (
              <p className="required" style={{ marginTop: 8 }}>
                {submitError}
              </p>
            )}

            <div>
              <button type="button" onClick={btnBack}>
                {t[language].back}
              </button>

              <button type="submit" disabled={submitting} aria-busy={submitting}>
                {submitting ? t[language].sending : t[language].confirm}
              </button>
            </div>
          </div>
        </div>

        <h4 className="footerCopyright">
          {t[language].footer}
          <Link to="https://wcm.media" target="blank">
            Wildcat Media.
          </Link>
        </h4>
      </form>
    </div>
  );
}

export default App;

